import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Search, Trash2, ShieldCheck } from "lucide-react";
import { ImportPanel } from "./ImportPanel";
import { fmtDate } from "./shared";

// Base de contacts vérifiés une fois pour toutes (format, doublons, désinscrits, jetables, domaine), utilisée par les campagnes.
export const ProspectsBase = ({ refreshKey, onChange }) => {
  const [d, setD] = useState({ rows: [], total: 0, all: 0 });
  const [q, setQ] = useState("");
  const [rk, setRk] = useState(0);
  const [more, setMore] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/email-prospects", { params: { q, limit: 200 } }).then((r) => setD(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q, rk, refreshKey]);
  const loadMore = async () => {
    setMore(true);
    try { const r = await api.get("/admin/email-prospects", { params: { q, skip: d.rows.length, limit: 500 } }); setD({ ...d, rows: [...d.rows, ...r.data.rows] }); }
    catch { toast.error("Chargement impossible"); } finally { setMore(false); }
  };
  const refresh = () => { setRk((k) => k + 1); onChange?.(); };
  const remove = async (id) => { try { await api.delete(`/admin/email-prospects/${id}`); refresh(); } catch { toast.error("Suppression impossible"); } };
  const exportXlsx = async () => {
    try {
      const r = await api.get("/admin/email-prospects/export.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = "contacts-verifies.xlsx"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); }
  };

  return (
    <div className="space-y-3" data-testid="prospects-base">
      <div className="flex items-start gap-3 rounded-2xl border border-emerald-400/30 bg-emerald-500/[0.06] p-4">
        <ShieldCheck className="mt-0.5 shrink-0 text-emerald-400" size={22} />
        <div>
          <p className="text-sm font-semibold text-white"><span data-testid="prospects-count">{d.all.toLocaleString("fr-FR")}</span> contact(s) vérifié(s), prêts pour vos campagnes</p>
          <p className="text-xs text-muted-foreground">Chaque adresse est contrôlée à l'import : format, doublons, désinscrits, adresses jetables et existence du domaine de messagerie. Seules les adresses valides entrent dans la base. Cochez « Prospects importés » dans une campagne pour leur écrire.</p>
        </div>
      </div>
      <ImportPanel cid="base" onDone={refresh} />
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-white" data-testid="prospects-list-title">Contacts vérifiés <span className="text-xs font-normal text-muted-foreground">{d.total.toLocaleString("fr-FR")} résultat(s)</span></p>
          <div className="relative ml-auto">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher" className="h-8 w-56 rounded-md border border-white/15 bg-black/40 pl-7 pr-2 text-xs text-white outline-none" data-testid="prospects-search" />
          </div>
          <Button size="sm" variant="outline" className="h-8 rounded-md" onClick={exportXlsx} data-testid="prospects-export"><Download size={13} className="mr-1" /> Excel</Button>
        </div>
        <div className="max-h-[34rem] overflow-auto">
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 bg-[#141110] text-muted-foreground"><tr><th className="p-2">E-mail</th><th className="p-2">Nom</th><th className="p-2">Société</th><th className="p-2">Vérifié le</th><th className="p-2"></th></tr></thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.id} className="border-t border-white/5" data-testid="prospect-row">
                  <td className="p-2 text-white">{r.email}{r.unsubscribed && <span className="ml-2 text-rose-300">désinscrit</span>}</td>
                  <td className="p-2">{[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"}</td>
                  <td className="p-2">{r.company || "—"}</td>
                  <td className="p-2">{fmtDate(r.verified_at)}</td>
                  <td className="p-2 text-right"><button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-destructive" data-testid={`prospect-delete-${r.id}`}><Trash2 size={13} /></button></td>
                </tr>
              ))}
              {!d.rows.length && <tr><td colSpan={5} className="p-3 text-center text-muted-foreground">Aucun contact vérifié pour le moment.</td></tr>}
            </tbody>
          </table>
        </div>
        {d.rows.length < d.total && (
          <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
            <span>{d.rows.length.toLocaleString("fr-FR")} affiché(s) sur {d.total.toLocaleString("fr-FR")}</span>
            <Button size="sm" variant="outline" className="rounded-full" disabled={more} onClick={loadMore} data-testid="prospects-more">Voir 500 de plus</Button>
          </div>
        )}
      </div>
    </div>
  );
};
